import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goodsReceiptsApi, inventoryItemsApi, warehousesApi } from '../../api/warehouse';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { GOODS_RECEIPT_STATUS_LABELS } from './GoodsReceiptsListPage';
import { GOODS_RECEIPT_STAGES } from '../../config/workflow-stages';
import { formatDate } from '../../utils/formatDate';

export function GoodsReceiptWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const grnId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: grnData, isError } = useQuery({ queryKey: ['goods-receipt', grnId], queryFn: () => goodsReceiptsApi.get(grnId) });
  const grn = grnData?.data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['goods-receipt', grnId] });

  const { data: itemsData } = useQuery({ queryKey: ['goods-receipt-items', grnId], queryFn: () => goodsReceiptsApi.listItems(grnId) });
  const items = itemsData?.data ?? [];
  const { data: inventoryItemsData } = useQuery({ queryKey: ['inventory-items'], queryFn: () => inventoryItemsApi.list() });
  const inventoryItems = inventoryItemsData?.data ?? [];
  const itemsById = new Map(inventoryItems.map((it) => [it.id, it]));
  const { data: warehousesData } = useQuery({ queryKey: ['warehouses'], queryFn: () => warehousesApi.list() });
  const warehouses = warehousesData?.data ?? [];
  const warehousesById = new Map(warehouses.map((w) => [w.id, w]));
  const { data: docsData } = useQuery({ queryKey: ['goods-receipt-documents', grnId], queryFn: () => genericDocumentApi.listFor('goods_receipt', grnId) });

  const [itemForm, setItemForm] = useState({ itemId: '', quantityReceived: '', batchNumber: '', expiryDate: '' });
  const selectedItem = itemForm.itemId ? itemsById.get(Number(itemForm.itemId)) : undefined;
  const addItemMutation = useMutation({
    mutationFn: () => goodsReceiptsApi.addItem(grnId, {
      itemId: Number(itemForm.itemId), quantityReceived: Number(itemForm.quantityReceived),
      batchNumber: itemForm.batchNumber || undefined, expiryDate: itemForm.expiryDate || undefined,
    }),
    onSuccess: () => { showSuccess('Item added.'); setItemForm({ itemId: '', quantityReceived: '', batchNumber: '', expiryDate: '' }); queryClient.invalidateQueries({ queryKey: ['goods-receipt-items', grnId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const verifyMutation = useMutation({
    mutationFn: () => goodsReceiptsApi.verify(grnId),
    onSuccess: () => { showSuccess('Goods receipt verified and posted to stock.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const [assetForms, setAssetForms] = useState<Record<number, string[]>>({});
  const createAssetsMutation = useMutation({
    mutationFn: ({ itemId, serials }: { itemId: number; serials: string[] }) =>
      goodsReceiptsApi.createAssetsFromReceiptLine(grnId, itemId, serials.map((s) => ({ serialNumber: s }))),
    onSuccess: (result) => showSuccess(`${result.data.assetIds.length} asset(s) created.`),
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this goods receipt. Please try refreshing the page.</div></div>;
  if (!grn) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={grn.receipt_reference}
        readOnly={false}
        entityType="goods_receipt"
        entityId={grnId}
        fields={[
          { label: 'Warehouse', value: warehousesById.get(grn.warehouse_id)?.name ?? '-' },
          { label: 'Supplier', value: grn.supplier ?? '-' },
          { label: 'Received Date', value: formatDate(grn.received_date) },
          { label: 'Status', value: <StatusBadge status={grn.status} label={GOODS_RECEIPT_STATUS_LABELS[grn.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'items', label: 'Items', content: (
              <div>
                <SectionHeader title="Received Items" />
                <ul>
                  {items.map((i) => {
                    const invItem = itemsById.get(i.item_id);
                    return (
                      <li key={i.id} style={{ marginBottom: 8 }}>
                        {invItem?.name ?? `Item #${i.item_id}`} — {i.quantity_received} {invItem?.unit_of_measure ?? ''}
                        {i.quantity_rejected !== '0.00' && ` (${i.quantity_rejected} rejected)`}
                        {invItem?.is_asset_creating && grn.status === 'verified' && (
                          <div style={{ marginTop: 4 }}>
                            <input
                              placeholder="Serial numbers, comma-separated - one per unit received"
                              style={{ width: 320 }}
                              value={(assetForms[i.item_id] ?? []).join(', ')}
                              onChange={(e) => setAssetForms((s) => ({ ...s, [i.item_id]: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) }))}
                            />
                            <button
                              onClick={() => createAssetsMutation.mutate({ itemId: i.item_id, serials: assetForms[i.item_id] ?? [] })}
                              disabled={createAssetsMutation.isPending || !(assetForms[i.item_id]?.length)}
                            >
                              Create Assets
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {grn.status === 'draft' && (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); addItemMutation.mutate(); }}>
                    <select value={itemForm.itemId} onChange={(e) => setItemForm((s) => ({ ...s, itemId: e.target.value }))} required>
                      <option value="">Select item…</option>
                      {inventoryItems.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                    <input type="number" step="0.01" placeholder="Quantity received" value={itemForm.quantityReceived} onChange={(e) => setItemForm((s) => ({ ...s, quantityReceived: e.target.value }))} required />
                    {selectedItem?.track_batch === 1 && (
                      <input placeholder="Batch number" value={itemForm.batchNumber} onChange={(e) => setItemForm((s) => ({ ...s, batchNumber: e.target.value }))} />
                    )}
                    {selectedItem?.track_expiry === 1 && (
                      <input type="date" placeholder="Expiry date" value={itemForm.expiryDate} onChange={(e) => setItemForm((s) => ({ ...s, expiryDate: e.target.value }))} />
                    )}
                    <button type="submit" disabled={addItemMutation.isPending}>Add Item</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'verify', label: 'Verify & Post', content: (
              <div>
                <SectionHeader title="Verify & Post to Stock" />
                {grn.status === 'draft' && (
                  <>
                    <p className="muted">Verifying will post every item on this receipt to warehouse stock. This cannot be silently undone afterward.</p>
                    <button onClick={() => verifyMutation.mutate()} disabled={verifyMutation.isPending || items.length === 0}>Verify & Post to Stock</button>
                  </>
                )}
                {grn.status === 'verified' && <p className="muted">This receipt has been posted to stock.</p>}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('goods_receipt', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('goods_receipt', grnId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('goods_receipt', documentId)}
                queryKeyToInvalidate={['goods-receipt-documents', grnId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="goods_receipt" entityId={grnId} actionStageMap={GOODS_RECEIPT_STAGES} statusLabels={GOODS_RECEIPT_STATUS_LABELS} />
                <CommentsThread entityType="goods_receipt" entityId={grnId} section="warehouse" heading="Comments for this Receipt" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
